import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

/**
 * Keyboard layer. Linear-style: everything has a shortcut and every shortcut
 * is discoverable. `/` opens the command palette, `?` shows this sheet, and
 * `g`-prefixed letters jump between surfaces. Alt+1…5 / Alt+S are the direct
 * surface jumps brought back from v2.
 *
 * Typing in any input never triggers any of it — and on the atom reader the
 * ←/→ and ↑/↓ arrows are page-level navigation (previous/next atom,
 * previous/next subtopic), registered lazily by ReferencePage so the lab's
 * own transport keys stay untouched.
 */

const JUMPS: Array<{ key: string; label: string; href: string; altKey: string }> = [
  { key: 'h', label: 'Home · Today', href: '/', altKey: 'Alt 1' },
  { key: 'a', label: 'Atlas · full curriculum', href: '/atlas', altKey: 'Alt 2' },
  { key: 'l', label: 'Labs · interactive lessons', href: '/learn', altKey: 'Alt 3' },
  { key: 'p', label: 'Practice · problem queue', href: '/practice', altKey: 'Alt 4' },
  { key: 'r', label: 'Review · recall queue', href: '/review', altKey: 'Alt 5' },
  { key: 'm', label: 'Method · how the system works', href: '/playbook', altKey: '' }
];

const ALT_JUMPS: Record<string, string> = {
  '1': '/',
  '2': '/atlas',
  '3': '/learn',
  '4': '/practice',
  '5': '/review',
  s: '/settings'
};

export function useKeyboardLayer(openPalette: () => void) {
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    let gArmed = false;
    let gTimer = 0;

    const isTyping = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      return target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable;
    };

    const onKey = (event: KeyboardEvent) => {
      if (isTyping(event.target)) return;
      if (event.metaKey || event.ctrlKey) return;

      // Alt+1…5 / Alt+S: direct surface jumps (restored from v2).
      if (event.altKey && !event.shiftKey) {
        const href = ALT_JUMPS[event.key.toLowerCase()];
        if (href) {
          event.preventDefault();
          void navigate({ to: href });
          return;
        }
      }
      if (event.altKey) return;

      if (event.key === '/') {
        event.preventDefault();
        openPalette();
        return;
      }
      if (event.key === '?') {
        event.preventDefault();
        setSheetOpen((value) => !value);
        return;
      }
      if (event.key === 'Escape') {
        setSheetOpen(false);
        return;
      }
      if (gArmed) {
        const jump = JUMPS.find((entry) => entry.key === event.key.toLowerCase());
        gArmed = false;
        window.clearTimeout(gTimer);
        if (jump) {
          event.preventDefault();
          void navigate({ to: jump.href });
        }
        return;
      }
      if (event.key.toLowerCase() === 'g') {
        gArmed = true;
        gTimer = window.setTimeout(() => { gArmed = false; }, 1400);
      }
    };

    addEventListener('keydown', onKey);
    return () => {
      removeEventListener('keydown', onKey);
      window.clearTimeout(gTimer);
    };
  }, [navigate, openPalette]);

  useEffect(() => {
    const onOpen = () => setSheetOpen(true);
    addEventListener('cpu:shortcuts', onOpen);
    return () => removeEventListener('cpu:shortcuts', onOpen);
  }, []);

  return { sheetOpen, setSheetOpen };
}

export function ShortcutSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  if (!open) return null;
  return (
    <div className="shortcuts-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="shortcuts-sheet" role="dialog" aria-modal="true" aria-label="Keyboard shortcuts">
        <div className="shortcuts-head">
          <h2>Keyboard first</h2>
          <button className="focus-close" onClick={onClose} aria-label="Close shortcut sheet">Esc</button>
        </div>
        <p className="shortcuts-lede">Navigate, search and act without leaving the keyboard. Shortcuts work everywhere unless you're typing in a field.</p>
        <div className="shortcuts-grid">
          <section>
            <h3>Global</h3>
            <ul>
              {JUMPS.map((jump) => (
                <li key={jump.key}>
                  <kbd>{jump.altKey || 'G'}</kbd>
                  {jump.altKey ? <kbd>{jump.key.toUpperCase()}</kbd> : null}
                  <span>{jump.label}{jump.altKey ? '' : ' — press G, then the letter'}</span>
                </li>
              ))}
              <li><kbd>Alt</kbd><kbd>S</kbd><span>Go to Settings</span></li>
              <li><kbd>Ctrl</kbd><kbd>K</kbd><span>Command palette — search atoms, modules, problems, actions</span></li>
              <li><kbd>/</kbd><span>Open the palette</span></li>
              <li><kbd>?</kbd><span>This sheet</span></li>
            </ul>
          </section>
          <section>
            <h3>Jump to surface (vim-style)</h3>
            <ul>
              <li><kbd>G</kbd><kbd>A</kbd><span>Go to Atlas</span></li>
              <li><kbd>G</kbd><kbd>L</kbd><span>Go to Labs</span></li>
              <li><kbd>G</kbd><kbd>H</kbd><span>Go to Today</span></li>
              <li><kbd>G</kbd><kbd>P</kbd><span>Go to Practice</span></li>
              <li><kbd>G</kbd><kbd>R</kbd><span>Go to Review</span></li>
              <li><kbd>G</kbd><kbd>M</kbd><span>Go to Method</span></li>
            </ul>
          </section>
          <section>
            <h3>Atom reader</h3>
            <ul>
              <li><kbd>←</kbd><kbd>→</kbd><span>Previous / next atom</span></li>
              <li><kbd>↑</kbd><kbd>↓</kbd><span>Previous / next subtopic (section)</span></li>
              <li><kbd>F</kbd><span>Toggle reading focus</span></li>
              <li><kbd>D</kbd><span>Mark atom finished / not finished</span></li>
              <li><kbd>N</kbd><span>Open the note composer</span></li>
            </ul>
          </section>
          <section>
            <h3>Review session</h3>
            <ul>
              <li><kbd>Space</kbd><span>Reveal the answer</span></li>
              <li><kbd>1</kbd><kbd>2</kbd><kbd>3</kbd><kbd>4</kbd><span>Rate recall (forgot → easy)</span></li>
            </ul>
          </section>
          <section>
            <h3>Trace lab</h3>
            <ul>
              <li><kbd>←</kbd><kbd>→</kbd><span>Previous / next frame</span></li>
              <li><kbd>Space</kbd><span>Play / pause the trace</span></li>
              <li><kbd>Home</kbd><kbd>End</kbd><span>First / last frame</span></li>
            </ul>
          </section>
          <section>
            <h3>Reader</h3>
            <ul>
              <li><kbd>J</kbd><kbd>K</kbd><span>Scroll between blocks</span></li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
